import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  LayoutDashboard, Users, FolderKanban, CheckSquare, TicketCheck,
  Globe, Server, Shield, KeyRound, MessageSquare, FileText,
  DollarSign, Receipt, CreditCard, Briefcase, UserCog,
  Calendar, CalendarDays, Clock, Wallet, Bell, Settings, ChevronDown, Database,
  LogOut, Menu, X, ChevronRight, Search, Activity,
  AlertTriangle, CheckCircle2, Info, Sun, Moon, User, ClipboardList, Mail,
  Target, Phone, BarChart3, RotateCcw, Star, ListChecks, Sparkles, Inbox, Scale,
  Volume2, VolumeX, ShieldAlert
} from 'lucide-react';
import { InboxNotifyProvider, useInboxNotify } from '../../context/InboxNotifyContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_myzow-system/artifacts/qvuxw5eo_myzow-logo-red.png';

const mockNotifications = [
  { id: 1, type: 'warning', title: 'SSL Expiring Soon', message: 'startupxyz.io SSL expires in 3 days', time: '10 min ago', read: false, link: '/ssl' },
  { id: 2, type: 'info', title: 'New Leave Request', message: 'Vikram Singh applied for 5 days earned leave', time: '25 min ago', read: false, link: '/leaves' },
  { id: 3, type: 'success', title: 'Payment Received', message: 'TechCorp paid INV-2025-001 (₹5,31,000)', time: '1 hour ago', read: false, link: '/transactions' },
  { id: 4, type: 'warning', title: 'Invoice Overdue', message: 'INV-2025-003 from GlobalTech is overdue', time: '2 hours ago', read: true, link: '/invoices' },
  { id: 5, type: 'info', title: 'Ticket Assigned', message: 'TKT-003 assigned to Vikram Singh', time: '3 hours ago', read: true, link: '/tickets' },
  { id: 6, type: 'success', title: 'Task Completed', message: 'CI/CD pipeline setup marked as done', time: '5 hours ago', read: true, link: '/tasks' },
];

const searchIndex = [
  { type: 'Client', name: 'TechCorp Industries', link: '/clients', icon: Users },
  { type: 'Client', name: 'StartupXYZ', link: '/clients', icon: Users },
  { type: 'Client', name: 'GlobalTech Solutions', link: '/clients', icon: Users },
  { type: 'Client', name: 'InnovateLabs', link: '/clients', icon: Users },
  { type: 'Project', name: 'E-commerce Platform', link: '/projects', icon: FolderKanban },
  { type: 'Project', name: 'Mobile App Development', link: '/projects', icon: FolderKanban },
  { type: 'Project', name: 'Website Redesign', link: '/projects', icon: FolderKanban },
  { type: 'Task', name: 'API Integration', link: '/tasks', icon: CheckSquare },
  { type: 'Task', name: 'Fix login bug', link: '/tasks', icon: CheckSquare },
  { type: 'Task', name: 'Design homepage mockup', link: '/tasks', icon: CheckSquare },
  { type: 'Invoice', name: 'INV-2025-001 — TechCorp', link: '/invoices', icon: Receipt },
  { type: 'Invoice', name: 'INV-2025-002 — StartupXYZ', link: '/invoices', icon: Receipt },
  { type: 'Invoice', name: 'INV-2025-003 — GlobalTech', link: '/invoices', icon: Receipt },
  { type: 'Employee', name: 'Rahul Sharma', link: '/employees', icon: Users },
  { type: 'Employee', name: 'Priya Patel', link: '/employees', icon: Users },
  { type: 'Employee', name: 'Amit Kumar', link: '/employees', icon: Users },
  { type: 'Domain', name: 'techcorp.com', link: '/domains', icon: Globe },
  { type: 'Domain', name: 'startupxyz.io', link: '/domains', icon: Globe },
  { type: 'Lead', name: 'NovaTech Pvt Ltd', link: '/leads', icon: Briefcase },
  { type: 'Lead', name: 'CloudNine Systems', link: '/leads', icon: Briefcase },
  { type: 'Ticket', name: 'TKT-001 — Login page not loading', link: '/tickets', icon: TicketCheck },
  { type: 'Ticket', name: 'TKT-003 — SSL certificate expired', link: '/tickets', icon: TicketCheck },
  { type: 'Page', name: 'Dashboard', link: '/dashboard', icon: LayoutDashboard },
  { type: 'Page', name: 'Invoices', link: '/invoices', icon: Receipt },
  { type: 'Page', name: 'Transactions', link: '/transactions', icon: CreditCard },
  { type: 'Page', name: 'Payroll', link: '/payroll', icon: Wallet },
  { type: 'Page', name: 'Activity Log', link: '/activity-log', icon: Activity },
  { type: 'Page', name: 'Settings', link: '/settings', icon: Settings },
  { type: 'Page', name: 'Roles & Permissions', link: '/roles', icon: Shield },
];

const navigation = [
  {
    group: 'My Portal',
    items: [
      { name: 'My Dashboard', href: '/my-portal', icon: User, permission: null },
      { name: 'My Attendance', href: '/my-portal/attendance', icon: Clock, permission: null },
      { name: 'My Payslips', href: '/my-portal/payslips', icon: Wallet, permission: null },
      { name: 'My Leaves', href: '/my-portal/leave', icon: CalendarDays, permission: null },
      { name: 'My Profile', href: '/my-portal/profile', icon: ClipboardList, permission: null },
      { name: 'My EOD Notes', href: '/eod-notes', icon: ClipboardList, permission: null },
    ]
  },
  {
    group: 'Main',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: null },
      { name: 'To Do', href: '/todo', icon: ListChecks, permission: null },
      { name: 'Projects', href: '/projects', icon: FolderKanban, permission: 'projects.view' },
      { name: 'Tasks', href: '/tasks', icon: CheckSquare, permission: 'tasks.view' },
    ]
  },
  {
    group: 'Management',
    items: [
      { name: 'Clients', href: '/clients', icon: Users, permission: 'clients.view' },
      { name: 'Leads', href: '/leads', icon: Briefcase, permission: 'leads.view' },
      { name: 'Tickets', href: '/tickets', icon: TicketCheck, permission: 'tickets.view' },
      { name: 'WhatsApp Inbox', href: '/smb/inbox', icon: Inbox, anyPermission: ['whatsapp.inbox.smb', 'whatsapp.inbox.general', 'smb.inbox'] },
    ]
  },
  {
    group: 'Myzow SMB',
    items: [
      { name: 'SMB Leads', href: '/smb/leads', icon: Target, permission: 'smb.view' },
      // Activation Pipeline temporarily hidden from the sidebar
      // (June 2026 — UX decision) per chat agreement. The route +
      // component stay live so super-admins can still reach it via
      // direct URL (`/smb/activation-pipeline`) and to avoid breaking
      // bookmarks. Re-enable by un-commenting below.
      // { name: 'Activation Pipeline', href: '/smb/activation-pipeline', icon: FolderKanban, permission: 'smb.view' },
      // Pipeline temporarily hidden from the sidebar (June 2026) — the
      // route + component are intentionally kept so it can be brought
      // back without re-implementing. To re-enable, uncomment below.
      // { name: 'Pipeline', href: '/smb/pipeline', icon: FolderKanban, permission: 'smb.view' },
      { name: 'Follow-ups', href: '/smb/followups', icon: Phone, permission: 'smb.view' },
      { name: 'Performance', href: '/smb/dashboard', icon: BarChart3, permission: 'smb.performance' },
      { name: 'Assignment Rules', href: '/smb/assignment-rules', icon: RotateCcw, permission: 'smb.assignment_rules' },
      { name: 'Outreach Automation', href: '/smb/outreach-templates', icon: Sparkles, permission: 'smb.outreach' },
    ]
  },
  {
    group: 'Assets',
    items: [
      { name: 'Domains', href: '/domains', icon: Globe, permission: 'domains.view' },
      { name: 'Hosting', href: '/hosting', icon: Server, permission: 'hosting.view' },
      { name: 'SSL Certificates', href: '/ssl', icon: Shield, permission: 'domains.view' },
      { name: 'Credentials', href: '/credentials', icon: KeyRound, permission: 'credentials.view' },
      { name: 'AMC/Retainers', href: '/amc', icon: FileText, permission: 'invoices.view' },
      { name: 'DLT', href: '/dlt', icon: MessageSquare, permission: 'domains.view' },
    ]
  },
  {
    group: 'Finance',
    items: [
      { name: 'Invoices', href: '/invoices', icon: Receipt, permission: 'invoices.view' },
      { name: 'Payment Links', href: '/finance/payment-links', icon: CreditCard, permission: 'payment_links.view' },
      { name: 'Transactions', href: '/transactions', icon: CreditCard, permission: 'invoices.view' },
      { name: 'Statement', href: '/statement', icon: Scale, permission: 'invoices.view' },
      { name: 'Bank Accounts', href: '/bank-accounts', icon: Wallet, permission: 'invoices.view' },
      { name: 'GSTR Report', href: '/gstr-report', icon: FileText, permission: 'invoices.view' },
    ]
  },
  {
    group: 'HR',
    items: [
      { name: 'Employees', href: '/employees', icon: UserCog, permission: 'employees.view' },
      { name: 'Attendance', href: '/attendance', icon: Clock, permission: 'employees.view' },
      { name: 'Holidays', href: '/holidays', icon: CalendarDays, permission: 'employees.view' },
      { name: 'Leaves', href: '/leaves', icon: Calendar, permission: 'leaves.view' },
      { name: 'Payroll', href: '/payroll', icon: Wallet, permission: 'employees.view' },
      { name: 'Appraisals', href: '/appraisals', icon: Star, permission: 'employees.view' },
      { name: 'EOD Notes', href: '/eod-notes', icon: ClipboardList, permission: 'employees.view' },
    ]
  },
  {
    group: 'System',
    items: [
      { name: 'Users', href: '/users', icon: Users, permission: 'users.view' },
      { name: 'Roles', href: '/roles', icon: Shield, permission: 'roles.view' },
      { name: 'Activity Log', href: '/activity-log', icon: Clock, permission: 'settings.view' },
      { name: 'Email Config', href: '/settings/email', icon: Mail, permission: 'settings.view' },
      { name: 'Renewal Alerts', href: '/settings/renewals', icon: Bell, permission: 'settings.view' },
      { name: 'Legacy Migration', href: '/settings/migration', icon: Database, permission: 'migration.run', requiresDangerTools: true },
      { name: 'System Flags', href: '/settings/system-flags', icon: ShieldAlert, permission: 'settings.view', superAdminOnly: true },
      { name: 'Settings', href: '/settings', icon: Settings, permission: 'settings.view' },
    ]
  }
];

export const DashboardLayout = ({ children }) => (
  <InboxNotifyProvider>
    <DashboardLayoutInner>{children}</DashboardLayoutInner>
  </InboxNotifyProvider>
);

const DashboardLayoutInner = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const { dangerToolsEnabled } = useFeatureFlags();
  const { theme, toggleTheme } = useTheme();
  const { leadCount, muted, toggleMuted, desktopPermission, requestDesktopPermission } = useInboxNotify();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Forced first-login password change (legacy-migrated users default to
  // their mobile number as password and must change it before doing anything
  // else). We redirect to /profile and show a non-dismissible banner there.
  useEffect(() => {
    if (user?.must_reset_password && location.pathname !== '/profile') {
      navigate('/profile', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // Dynamic page title based on current route
  useEffect(() => {
    const titleMap = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/employees': 'Employees',
      '/projects': 'Projects',
      '/tasks': 'Tasks',
      '/tickets': 'Tickets',
      '/clients': 'Clients',
      '/leads': 'Leads',
      '/invoices': 'Invoices',
      '/raise-invoice': 'Raise Invoice',
      '/transactions': 'Transactions',
      '/statement': 'Statement',
      '/bank-accounts': 'Bank Accounts',
      '/attendance': 'Attendance',
      '/holidays': 'Holidays',
      '/leaves': 'Leaves',
      '/payroll': 'Payroll',
      '/appraisals': 'Appraisals',
      '/smb-leads': 'SMB Leads',
      '/smb-pipeline': 'SMB Pipeline',
      '/smb-followups': 'SMB Follow-ups',
      '/smb-dashboard': 'SMB Dashboard',
      '/smb-assignment-rules': 'SMB Assignment Rules',
      '/smb/leads': 'SMB Leads',
      '/smb/activation-pipeline': 'Activation Pipeline',
      '/smb/pipeline': 'SMB Pipeline',
      '/smb/followups': 'SMB Follow-ups',
      '/smb/dashboard': 'SMB Dashboard',
      '/smb/assignment-rules': 'SMB Assignment Rules',
      '/domains': 'Domains',
      '/hosting': 'Hosting',
      '/ssl': 'SSL Certificates',
      '/amc': 'AMC',
      '/dlt': 'DLT',
      '/renewal-alerts': 'Renewal Alerts',
      '/credentials': 'Credentials',
      '/gstr-reports': 'GSTR Reports',
      '/email-settings': 'Email Settings',
      '/roles': 'Roles',
      '/settings': 'Settings',
      '/profile': 'My Profile',
      '/activity-log': 'Activity Log',
      '/my-portal': 'My Portal',
      '/my-attendance': 'My Attendance',
      '/my-leaves': 'My Leaves',
      '/my-payslips': 'My Payslips',
      '/my-profile': 'My Profile',
      '/eod-notes': 'EOD Task Notes',
      '/todo': 'To Do',
    };
    const path = location.pathname;
    const title = titleMap[path]
      || (path.startsWith('/employees/') ? 'Employee Details' : null)
      || (path.startsWith('/smb-leads/') ? 'SMB Lead Details' : null)
      || (path.startsWith('/smb/leads/') ? 'SMB Lead Details' : null)
      || 'Dashboard';
    document.title = `${title} | Myzow ERP 2.0`;
  }, [location.pathname]);

  // Filter navigation based on permissions and user type
  const filteredNavigation = useMemo(() => {
    const isSuperAdmin = !!user?.permissions?.includes('*');
    return navigation
      .map(section => {
        // My Portal only visible for employee-linked users
        if (section.group === 'My Portal' && !user?.is_employee) return { ...section, items: [] };
        return {
          ...section,
          items: section.items.filter(item => {
            if (item.permission && !hasPermission(item.permission)) return false;
            // Allow ANY-of semantics: item.anyPermission is an array;
            // entry shows if the user has at least one of those keys.
            if (item.anyPermission && !item.anyPermission.some(p => hasPermission(p))) return false;
            // Sidebar item declares `requiresDangerTools` → only show when
            // the feature flag is ON (Legacy Migration).
            if (item.requiresDangerTools && !dangerToolsEnabled) return false;
            // `superAdminOnly` for System Flags — the page itself also
            // gates, but hiding the sidebar entry avoids confusion.
            if (item.superAdminOnly && !isSuperAdmin) return false;
            return true;
          }),
        };
      })
      .filter(section => section.items.length > 0);
  }, [hasPermission, user, dangerToolsEnabled]);

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Myzow" className="h-8" />
            </Link>
            <Button 
              variant="ghost" 
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-6">
              {filteredNavigation.map((section) => (
                <div key={section.group}>
                  <button
                    onClick={() => toggleGroup(section.group)}
                    className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                  >
                    {section.group}
                    <ChevronRight 
                      className={`h-3 w-3 transition-transform ${
                        collapsedGroups[section.group] ? '' : 'rotate-90'
                      }`} 
                    />
                  </button>
                  {!collapsedGroups[section.group] && (
                    <div className="mt-1 space-y-1">
                      {section.items.map((item) => {
                        const isActive = location.pathname === item.href;
                        const showInboxBadge = item.href === '/smb/inbox' && leadCount > 0;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
                            data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <item.icon className="h-4 w-4" />
                            <span className="flex-1">{item.name}</span>
                            {showInboxBadge && (
                              <span
                                className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none"
                                data-testid="inbox-unread-badge"
                              >
                                {leadCount > 99 ? '99+' : leadCount}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </ScrollArea>

          {/* Role Badge + User Menu at Bottom */}
          <div className="border-t border-border">
            {/* Role Badge */}
            <div className="px-4 pt-3 pb-2">
              <div
                className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg bg-primary/5 border border-primary/20"
                data-testid="role-badge"
              >
                <Shield className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="text-xs font-medium">{user?.roles?.[0]?.name || 'User'}</span>
              </div>
            </div>
            <div className="px-4 pb-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}` : undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xs">
                      {getInitials(user?.first_name, user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <UserCog className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                  data-testid="logout-button"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 lg:px-8 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
            {/* Global Search */}
            <GlobalSearch navigate={navigate} />
          </div>

          <div className="flex items-center gap-2">
            {/* Inbox sound mute toggle — only for users with WhatsApp Inbox access.
                On the FIRST click while desktop-notification permission is still
                'default', also opens the browser's permission prompt — this is
                the only way to request permission inside a user gesture. */}
            {(hasPermission('whatsapp.inbox.smb') || hasPermission('whatsapp.inbox.general') || hasPermission('smb.inbox')) && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (desktopPermission === 'default') {
                    requestDesktopPermission().catch(() => {});
                  }
                  toggleMuted();
                }}
                className="relative"
                data-testid="inbox-sound-toggle"
                title={
                  desktopPermission === 'default'
                    ? 'Click to enable desktop alerts for new WhatsApp messages (also toggles sound)'
                    : muted
                      ? 'WhatsApp inbox sound is muted — click to unmute'
                      : 'WhatsApp inbox sound is on — click to mute'
                }
              >
                {muted ? <VolumeX className="h-5 w-5 text-muted-foreground" /> : <Volume2 className="h-5 w-5" />}
                {desktopPermission === 'default' && (
                  <span
                    className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-background"
                    aria-hidden="true"
                  />
                )}
              </Button>
            )}
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="relative"
              data-testid="theme-toggle-button"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            {/* Notifications */}
            <NotificationCenter navigate={navigate} />
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};


const GlobalSearch = ({ navigate }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [leadResults, setLeadResults] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Live SMB lead search — debounced. Hits `/api/smb/leads?search=…`
  // which already matches against business_name, contact_name, phone,
  // area, AND smb_project_id (both the 8-char short code and the full
  // UUID, case-insensitive). So operators can paste the PID a customer
  // quotes them and jump straight to the lead from anywhere in the app.
  const fetchLeads = useCallback(async (q) => {
    setLeadsLoading(true);
    try {
      const { default: api } = await import('../../lib/api');
      const r = await api.get(`/smb/leads?search=${encodeURIComponent(q)}&page_size=6`);
      const items = (r.data?.data || []).map(l => ({
        type: 'SMB Leads',
        name: l.business_name || '(no name)',
        subtitle: l.smb_project_id
          ? `PID ${String(l.smb_project_id).slice(-8).toUpperCase()} · ${l.contact_name || ''}`.trim()
          : (l.contact_name || l.phone || l.area || ''),
        link: `/smb/leads/${l.id}`,
        icon: Target,
      }));
      setLeadResults(items);
    } catch {
      setLeadResults([]);
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 3) return;  // results auto-clear via memo below
    debounceRef.current = setTimeout(() => fetchLeads(q), 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchLeads]);

  // Suppress stale lead results when the query is short — avoids
  // setting state from inside the effect just to clear them.
  const effectiveLeadResults = query.trim().length < 3 ? [] : leadResults;

  const staticResults = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return searchIndex.filter(item => item.name.toLowerCase().includes(q)).slice(0, 8);
  }, [query]);

  const results = useMemo(() => [...staticResults, ...effectiveLeadResults], [staticResults, effectiveLeadResults]);

  const grouped = useMemo(() => {
    const g = {};
    results.forEach(r => { if (!g[r.type]) g[r.type] = []; g[r.type].push(r); });
    return g;
  }, [results]);

  const handleSelect = (item) => {
    navigate(item.link);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative hidden sm:block" ref={ref}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search anything (name, phone, project ID)..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { if (query.length >= 2) setOpen(true); }}
          className="pl-10 w-64 lg:w-80 h-9 bg-muted/30"
          data-testid="global-search"
        />
        {query && <button onClick={() => { setQuery(''); setOpen(false); }} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="h-3.5 w-3.5 text-muted-foreground" /></button>}
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50" data-testid="search-results">
          <ScrollArea className="max-h-80">
            {Object.entries(grouped).map(([type, items]) => (
              <div key={type}>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase px-3 pt-2 pb-1">{type}</p>
                {items.map(item => (
                  <button key={`${type}-${item.name}-${item.link}`} onClick={() => handleSelect(item)}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm hover:bg-muted/40 transition-colors text-left"
                    data-testid={`gsearch-item-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                    <item.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <span className="truncate block">{item.name}</span>
                      {item.subtitle && (
                        <span className="text-[10px] text-muted-foreground font-mono truncate block">{item.subtitle}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </ScrollArea>
        </div>
      )}
      {open && query.length >= 2 && results.length === 0 && !leadsLoading && (
        <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-lg shadow-xl p-4 text-sm text-muted-foreground text-center z-50">No results for &quot;{query}&quot;</div>
      )}
      {open && query.length >= 3 && leadsLoading && results.length === 0 && (
        <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-lg shadow-xl p-4 text-sm text-muted-foreground text-center z-50">Searching…</div>
      )}
    </div>
  );
};

const NotificationCenter = ({ navigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const { default: api } = await import('../../lib/api');
        const res = await api.get('/notifications');
        if (res.data.success) {
          setNotifications(res.data.data);
          setUnreadCount(res.data.unread_count);
        }
      } catch {}
    };
    fetchNotifications();
  }, []);

  const markAllRead = async () => {
    try {
      const { default: api } = await import('../../lib/api');
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        const { default: api } = await import('../../lib/api');
        await api.post(`/notifications/${n.id}/read`);
      } catch {}
    }
    setNotifications(prev => prev.map(nt => nt.id === n.id ? { ...nt, read: true } : nt));
    setUnreadCount(prev => n.read ? prev : Math.max(0, prev - 1));
    if (n.link) navigate(n.link);
  };

  const getIcon = (type) => {
    if (type === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    if (type === 'success') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    return <Info className="h-4 w-4 text-blue-500" />;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-primary rounded-full text-[10px] font-bold text-white flex items-center justify-center">{unreadCount}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96" data-testid="notification-panel">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && <button onClick={markAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read">Mark all read</button>}
            <button onClick={() => navigate('/settings')} className="text-muted-foreground hover:text-foreground transition-colors" title="Notification Preferences" data-testid="notif-settings-link"><Settings className="h-4 w-4" /></button>
          </div>
        </div>
        <DropdownMenuSeparator />
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="notif-bell-empty">You&apos;re all caught up.</div>
          ) : notifications.map(n => (
            <button key={n.id} onClick={() => handleClick(n)} className={`flex items-start gap-3 w-full px-3 py-3 text-left hover:bg-muted/40 transition-colors ${!n.read ? 'bg-primary/5' : ''}`} data-testid={`notification-${n.id}`}>
              <div className="mt-0.5 shrink-0">{getIcon(n.type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm ${!n.read ? 'font-semibold' : 'font-medium'}`}>{n.title}</p>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground truncate">{n.message}</p>
                <p className="text-[10px] text-muted-foreground/60 mt-0.5">{n.time}</p>
              </div>
            </button>
          ))}
        </ScrollArea>
        <DropdownMenuSeparator />
        <button
          onClick={() => navigate('/notifications')}
          className="w-full px-3 py-2.5 text-xs font-medium text-primary hover:bg-muted/40 transition-colors text-center"
          data-testid="notif-view-all"
        >
          View all notifications
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
